import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { favoritesApi } from '../services/api/favorites';
import { useSnackbar } from 'notistack';

export const useFavorites = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  // Get favorites
  const { data, isLoading, error } = useQuery({
    queryKey: ['favorites'],
    queryFn: () => favoritesApi.getFavorites({ include_details: true, sort: 'custom' }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const favorites = data?.favorites || [];
  const favoriteIds = new Set(favorites.map((f) => f.market?.id).filter((id) => id !== undefined));

  // Check if market is favorited
  const isFavorite = (marketId: number) => {
    return favoriteIds.has(marketId);
  };

  // Add favorite mutation
  const addMutation = useMutation({
    mutationFn: (marketId: number) => favoritesApi.addFavorite(marketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
      enqueueSnackbar('Added to favorites', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to add favorite', { variant: 'error' });
    },
  });

  // Remove favorite mutation with optimistic update
  const removeMutation = useMutation({
    mutationFn: (marketId: number) => favoritesApi.removeFavorite(marketId),
    onMutate: async (marketId: number) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['favorites'] });

      // Snapshot the previous value for rollback
      const previousFavorites = queryClient.getQueryData(['favorites']);

      // Optimistically update by removing the favorite from cache
      queryClient.setQueryData(['favorites'], (old: any) => {
        if (!old?.favorites) return old;
        return {
          ...old,
          favorites: old.favorites.filter((f: any) => f.market?.id !== marketId),
          total: old.total - 1,
        };
      });

      // Return context with previous value for rollback
      return { previousFavorites };
    },
    onSuccess: () => {
      // Invalidate to sync with server, but don't show snackbar for remove
      queryClient.invalidateQueries({ queryKey: ['favorites'] });
    },
    onError: (_error, _marketId, context) => {
      // Rollback to previous value on error
      if (context?.previousFavorites) {
        queryClient.setQueryData(['favorites'], context.previousFavorites);
      }
      enqueueSnackbar('Failed to remove favorite', { variant: 'error' });
    },
  });

  // Toggle favorite
  const toggleFavorite = async (marketId: number) => {
    if (isFavorite(marketId)) {
      await removeMutation.mutateAsync(marketId);
    } else {
      await addMutation.mutateAsync(marketId);
    }
  };

  return {
    favorites,
    isLoading,
    error,
    isFavorite,
    addFavorite: addMutation.mutate,
    removeFavorite: removeMutation.mutate,
    toggleFavorite,
    isUpdating: addMutation.isPending || removeMutation.isPending,
  };
};
